import React, { useEffect, useRef, useState } from "react";
import { Dialog } from '@headlessui/react';
import { useTheme } from '../../CommonComponents/ThemeProvider';
import { Minus, PackagePlus, Plus, Send, X } from "lucide-react";

export default function AddStockModal({ isOpen, onClose, inventory, onSave, selectedBranchId = "" }) {
    const [addQuantity, setAddQuantity] = useState(0);
    const [error, setError] = useState("");
    const quantityInputRef = useRef(null);
    const { currentTheme } = useTheme();
    const stockLabel = selectedBranchId ? "Stocks Released" : "Stocks Available";
    const actionLabel = selectedBranchId ? "Release Stock" : "Add Stock";
    const quantityLabel = selectedBranchId ? "Release Quantity" : "Add Quantity";
    const quantityPlaceholder = selectedBranchId ? "Enter quantity to release" : "Enter quantity to add";
    const description = selectedBranchId
        ? "Release stock to the selected branch without leaving the page."
        : "Increase the current inventory quantity without leaving the page.";
    const ActionIcon = selectedBranchId ? Send : PackagePlus;
    const warehouseAvailable = Number(inventory?.quantity) || 0;

    useEffect(() => {
        if (isOpen) {
            setAddQuantity(0);
            setError("");
        }
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) return;

        const focusQuantityInput = () => {
            quantityInputRef.current?.focus({ preventScroll: true });
            quantityInputRef.current?.select();
        };

        const frameId = window.requestAnimationFrame(focusQuantityInput);
        const timeoutId = window.setTimeout(focusQuantityInput, 80);

        return () => {
            window.cancelAnimationFrame(frameId);
            window.clearTimeout(timeoutId);
        };
    }, [isOpen, inventory?.id, selectedBranchId]);

    if (!isOpen || !inventory) return null;

    const currentStock = selectedBranchId
        ? (Number(inventory.released_quantity) || 0)
        : (Number(inventory.quantity) || 0);
    const totalStock = currentStock + (Number(addQuantity) || 0);
    const maxFillQuantity = selectedBranchId ? warehouseAvailable : 0;
    const canUseMax = maxFillQuantity > 0;
    const numericAddQuantity = Math.max(Number(addQuantity) || 0, 0);
    const adjustQuantity = (delta) => {
        const next = Math.max(numericAddQuantity + delta, 0);
        setAddQuantity(selectedBranchId && maxFillQuantity > 0 ? Math.min(next, maxFillQuantity) : next);
        setError("");
    };

    const handleUseMax = () => {
        if (!canUseMax) return;
        setAddQuantity(maxFillQuantity);
        setError("");
    };

    const handleSave = () => {
        const parsed = Number(addQuantity);
        if (!Number.isFinite(parsed) || parsed <= 0) {
            setError("Please enter a valid quantity greater than 0.");
            return;
        }
        if (selectedBranchId && parsed > warehouseAvailable) {
            setError(`Only ${warehouseAvailable} item${warehouseAvailable === 1 ? "" : "s"} available in warehouse.`);
            return;
        }
        onSave(parsed);
    };

    const handleQuantityKeyDown = (event) => {
        if (event.key !== "Enter") return;
        event.preventDefault();
        handleSave();
    };

    return (
        <Dialog
            open={isOpen}
            onClose={onClose}
            className="fixed inset-0 z-50 flex items-center justify-center overflow-hidden bg-slate-950/45 px-4 backdrop-blur-sm"
        >
            <div className="w-full max-w-md">
                <Dialog.Panel className="relative flex max-h-[90vh] flex-col overflow-hidden rounded-[28px] border border-white/70 bg-white shadow-[0_30px_80px_rgba(15,23,42,0.18)]">
                    <div className="pointer-events-none absolute inset-x-0 top-0 h-28 bg-[linear-gradient(135deg,rgba(15,23,42,0.05),rgba(148,163,184,0.04),transparent)]" />
                    <button
                        type="button"
                        onClick={onClose}
                        className="absolute right-4 top-4 z-10 inline-flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white/90 text-slate-500 shadow-sm transition hover:text-slate-900"
                        aria-label={`Close ${actionLabel.toLowerCase()} modal`}
                    >
                        <X className="h-5 w-5" />
                    </button>

                    <div className="relative shrink-0 px-6 pb-4 pt-6 md:px-7 md:pt-7">
                        <div className="flex items-start gap-4 pr-12">
                            <div className="inline-flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl text-white shadow-sm" style={{ background: currentTheme.color }}>
                                <ActionIcon className="h-6 w-6" />
                            </div>
                            <div className="min-w-0">
                                <Dialog.Title className="text-2xl font-semibold tracking-tight text-slate-950">
                                    {actionLabel}
                                </Dialog.Title>
                                <p className="mt-1 text-sm leading-6 text-slate-500">
                                    {description}
                                </p>
                                {selectedBranchId && (
                                    <p className="mt-2 inline-flex rounded-full border border-amber-100 bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-700">
                                        Warehouse available: {warehouseAvailable}
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="relative min-h-0 overflow-y-auto overflow-x-hidden px-6 pb-6 md:px-7 md:pb-7">
                        <div className="space-y-3 rounded-[24px] border border-slate-200 bg-slate-50/80 p-4 text-left">
                            <div className="rounded-[22px] border border-slate-200 bg-slate-100/80 px-4 py-3">
                                <div className="flex items-start justify-between gap-3">
                                    <div>
                                        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Current {stockLabel}</p>
                                        <p className="mt-2 text-3xl font-semibold text-slate-900">{currentStock}</p>
                                    </div>
                                    <span className="rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400">
                                        Read-only
                                    </span>
                                </div>
                                <p className="mt-1 text-xs text-slate-500">Before this adjustment</p>
                            </div>
                            <div>
                                <label className="block text-xs font-semibold uppercase tracking-[0.18em] text-slate-400 mb-2">{quantityLabel}</label>
                                <div
                                    className="flex h-16 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition focus-within:ring-4"
                                    style={{
                                        boxShadow: "0 8px 20px rgba(15,23,42,0.04)",
                                        "--tw-ring-color": "color-mix(in srgb, var(--color-primary) 14%, transparent)",
                                    }}
                                >
                                    <button
                                        type="button"
                                        onClick={() => adjustQuantity(-1)}
                                        disabled={numericAddQuantity <= 0}
                                        className="flex w-14 shrink-0 items-center justify-center border-r border-slate-200 text-slate-500 transition hover:bg-slate-50 hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-40"
                                        aria-label={`Decrease ${quantityLabel.toLowerCase()}`}
                                    >
                                        <Minus className="h-4 w-4" />
                                    </button>
                                    <input
                                        ref={quantityInputRef}
                                        type="number"
                                        className="no-number-spinner min-w-0 flex-1 border-0 bg-white px-3 text-center text-lg font-semibold text-slate-900 outline-none"
                                        value={addQuantity}
                                        min={0}
                                        step={1}
                                        inputMode="numeric"
                                        max={selectedBranchId ? warehouseAvailable : undefined}
                                        onKeyDown={handleQuantityKeyDown}
                                        onChange={e => {
                                            setAddQuantity(e.target.value === "" ? "" : Number(e.target.value));
                                            setError("");
                                        }}
                                        placeholder={quantityPlaceholder}
                                    />
                                    {selectedBranchId && (
                                        <button
                                            type="button"
                                            onClick={handleUseMax}
                                            disabled={!canUseMax}
                                            className="flex w-14 shrink-0 items-center justify-center border-l border-slate-200 text-[11px] font-bold uppercase tracking-[0.12em] text-slate-600 transition hover:bg-slate-50 hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-40"
                                        >
                                            Max
                                        </button>
                                    )}
                                    <button
                                        type="button"
                                        onClick={() => adjustQuantity(1)}
                                        disabled={selectedBranchId && numericAddQuantity >= maxFillQuantity}
                                        className="flex w-14 shrink-0 items-center justify-center border-l border-slate-200 text-slate-500 transition hover:bg-slate-50 hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-40"
                                        aria-label={`Increase ${quantityLabel.toLowerCase()}`}
                                    >
                                        <Plus className="h-4 w-4" />
                                    </button>
                                </div>
                            </div>
                            <div className="rounded-[22px] border border-emerald-100 bg-emerald-50 px-4 py-3">
                                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-emerald-700">New {stockLabel}</p>
                                <p className="mt-2 text-2xl font-semibold text-emerald-800">
                                    {Number.isFinite(totalStock) ? totalStock : currentStock}
                                </p>
                            </div>
                        </div>

                        {error && (
                            <div className="rounded-2xl border border-rose-100 bg-rose-50 px-4 py-3 text-sm text-rose-600">
                                {error}
                            </div>
                        )}

                        <div className="flex justify-end gap-3 pt-4">
                            <button
                                onClick={onClose}
                                className="rounded-2xl border border-slate-200 bg-slate-100 px-4 py-2.5 text-sm font-medium text-slate-700 transition hover:bg-slate-200 focus:outline-none"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSave}
                                className="rounded-2xl px-5 py-2.5 text-sm font-semibold text-white shadow-[0_14px_30px_rgba(15,23,42,0.16)] transition hover:opacity-90 focus:outline-none"
                                style={{ background: `linear-gradient(135deg, ${currentTheme.color}, color-mix(in srgb, ${currentTheme.color} 78%, black))` }}
                            >
                                {actionLabel}
                            </button>
                        </div>
                    </div>
                </Dialog.Panel>
            </div>
        </Dialog>
    );
}
